// Cutting optimizer: beams of different lengths cut from standard glulam beams of 12.00 or 13.50 m.
// Every cut takes 1 cm (KERF), including one trimming cut at each end of the standard beam:
// k beams from one standard beam need sum(lengths) + (k + 1) × 1 cm.
// Search: for every possible number x of 13.50 m beams, pack longest-first with best fit (x long beams,
// then 12.00 m beams), shrink long beams whose content fits in 12.00 m, keep the plan with the fewest
// metres to order (then fewest beams). Same rules as backend/calc_pdf.py optimize_cuts.
export const STOCK = [12, 13.5];
export const KERF = 0.01;
export const MAX_PIECES = 500;
const EPS = 1e-9;
const used = (bar) => bar.sum + (bar.pieces.length + 1) * KERF;
const fits = (bar, len) => used(bar) + len + KERF <= bar.stock + EPS;

const pack = (pieces, x) => {
  const bars = [];
  let long = 0;
  for (const len of pieces) {
    let best = null;
    for (const bar of bars) if (fits(bar, len) && (!best || bar.stock - used(bar) < best.stock - used(best))) best = bar;
    if (!best) {
      const stock = long < x ? STOCK[1] : STOCK[0];
      if (len + 2 * KERF > stock + EPS) return null;
      if (stock === STOCK[1]) long += 1;
      best = { stock, pieces: [], sum: 0 };
      bars.push(best);
    }
    best.pieces.push(len);
    best.sum += len;
  }
  for (const bar of bars) if (bar.stock === STOCK[1] && used(bar) <= STOCK[0] + EPS) bar.stock = STOCK[0];
  return { bars, long };
};

const fmtKey = (bar) => `${bar.stock}|${bar.pieces.join("+")}`;

// rows: [{ n, len }] → { over: [lengths above standard], groups, bars, metres, waste, wastePct } or null
export const optimizeCuts = (rows) => {
  const clean = rows.filter((r) => r.n >= 1 && r.len > 0);
  if (!clean.length) return null;
  const maxLen = STOCK[1] - 2 * KERF;
  const over = [...new Set(clean.filter((r) => r.len > maxLen + EPS).map((r) => r.len))];
  const pieces = clean.filter((r) => r.len <= maxLen + EPS).flatMap((r) => Array(r.n).fill(r.len)).sort((a, b) => b - a);
  if (!pieces.length) return { over, groups: [], bars: 0, metres: 0, waste: 0, wastePct: 0 };
  let best = null;
  for (let x = 0; x <= pieces.length; x += 1) {
    const res = pack(pieces, x);
    if (!res) continue;
    const { bars } = res;
    const metres = bars.reduce((s, b) => s + b.stock, 0);
    if (!best || metres < best.metres - EPS || (Math.abs(metres - best.metres) < EPS && bars.length < best.bars.length)) best = { bars, metres };
    if (res.long < x) break; // more long beams allowed cannot change the plan
  }
  const groups = [];
  for (const bar of best.bars) {
    const key = fmtKey(bar);
    const g = groups.find((y) => y.key === key);
    if (g) g.count += 1;
    else groups.push({ key, stock: bar.stock, pieces: bar.pieces, count: 1, waste: bar.stock - bar.sum });
  }
  const net = pieces.reduce((s, l) => s + l, 0);
  return { over, groups, bars: best.bars.length, metres: best.metres, waste: best.metres - net, wastePct: ((best.metres - net) / best.metres) * 100 };
};
